/* eslint-disable react/no-array-index-key */

import { TableData, TableProps } from '../types/table.type';
import useTableSort from '../hooks/useTableSort';
/**
 * Minimum usage:
 * <Table
 *   data={[{id: 1, name: "John"}, {id: 2, name: "Jane"}]}
 *   columns={[
 *     {key: "id", header: "ID"},
 *     {key: "name", header: "Name"}
 *   ]}
 * />
 *
 * Requirements:
 * - data: Array of objects (must extend TableData with at least an 'id' property)
 * - columns: Array of column definitions with 'key' and 'header' properties where the key matches the object keys in data.
 * Note: if you don't set a title the count for the table will appear where the title would be.
 */
export default function Table<T extends TableData>({
  data,
  columns,
  actionColumns = [],
  title,
  loading = false,
  emptyMessage = 'No data available',
  onRowClick,
  rowClassName,
  tableClassName = '',
  tHeaderClassName = '',
  tBodyClassName = '',
  showCount = true,
  countLabel = 'items',
  maxHeightClass = '',
}: TableProps<T>) {
  const { sortedData, handleSort } = useTableSort<T>(data);

  const totalColumns = columns.length + actionColumns.length;

  const getRowClassName = (row: T, index: number): string => {
    let className = '';
    if (rowClassName) {
      className += rowClassName(row, index);
    }
    if (onRowClick) {
      className += 'cursor-pointer';
    }
    return className;
  };

  const handleRowClick = (row: T, index: number) => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded-lg">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" />
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className={`bg-white rounded-lg overflow-hidden ${tableClassName}`}>
        {(title || showCount) && (
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              {title && (
                <h2 className="text-lg font-medium text-gray-900">{title}</h2>
              )}
              {showCount && (
                <span className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full">
                  {data.length}
                  {' '}
                  {data.length === 1 ? countLabel.slice(0, -1) : countLabel}
                </span>
              )}
            </div>
          </div>
        )}
        <div
          className={`overflow-auto ${maxHeightClass}`}
        >
          <table role="grid" className=" min-w-max w-full table-auto font-thin">
            <thead>
              <tr className={`${tHeaderClassName} bg-pipgrey-light border border-t-0 text-pip-text text-sm`}>
                {columns.map((column) => (
                  <th
                    key={String(column.key)}
                    scope="col"
                    title={column.sortable !== false ? `Click to sort by ${column.header}` : column.header}
                    className={`py-3 px-6 font-thin text-piptext-default text-left ${column.sortable ? 'cursor-pointer' : ''} ${column.headerClassName || ''}`}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        className="cursor-pointer"
                        onClick={() => handleSort(String(column.key))}
                      >
                        {column.header}
                      </button>
                    ) : column.header}

                  </th>
                ))}
                {actionColumns.map((actionColumn) => (
                  <th
                    key={actionColumn.key}
                    scope="col"
                    className={`py-3 px-6 font-thin text-piptext-default text-center cursor-default ${actionColumn.headerClassName || ''}`}
                  >
                    {actionColumn.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={`bg-white divide-y divide-gray-100 ${tBodyClassName}`}>
              {data.length === 0 ? (
                <tr>
                  <td
                    colSpan={totalColumns}
                    className="px-6 py-8 text-center text-piptext-default"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                sortedData.map((row, index) => (
                  <tr
                    key={row.id || index}
                    className={getRowClassName(row, index)}
                    onClick={() => handleRowClick(row, index)}
                  >
                    {columns.map((column) => {
                      const value = row[column.key];
                      return (
                        <td
                          key={`${String(column.key)}-${index}`}
                          role="gridcell"
                          className={`py-3 px-6 text-left ${column.className || ''}`}
                        >
                          {column.render ? column.render(value, row, index) : value || '—'}
                        </td>
                      );
                    })}
                    {actionColumns.map((actionColumn) => (
                      <td
                        key={`${actionColumn.key}-${index}`}
                        className={`py-3 px-6 text-center justify-around pointer-events-none ${actionColumn.className || ''}`}
                      >
                        {actionColumn.render(row, index)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
